import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { ChevronDown, Lock, EyeOff, Users, Check } from "lucide-react";

/**
 * Compact sharing toggle rendered in the CaseEditor header so the consumer
 * always sees whether their case is visible to attorneys and can change it
 * with a single click. The full copy/preview lives in the Share tab.
 */
const OPTIONS = [
  {
    value: "private",
    label: "Private",
    icon: Lock,
    tone: "border-slate-300 text-slate-700 bg-slate-50",
    blurb: "Only you can see this case",
  },
  {
    value: "anonymized",
    label: "Anonymized",
    icon: EyeOff,
    tone: "border-emerald-300 text-emerald-800 bg-emerald-50",
    blurb: "Attorneys see a redacted summary in the marketplace",
  },
  {
    value: "full",
    label: "Full (after intro)",
    icon: Users,
    tone: "border-blue-300 text-blue-800 bg-blue-50",
    blurb: "Full case shared only after you accept an intro",
  },
];

export default function CaseSharingBadge({ caseData, onChange }) {
  const [level, setLevel] = useState(caseData?.sharing_level || "private");
  const [busy, setBusy] = useState(false);

  const current = OPTIONS.find((o) => o.value === level) || OPTIONS[0];
  const Icon = current.icon;

  const setLevelRemote = async (newLevel) => {
    if (newLevel === level) return;
    setBusy(true);
    try {
      const r = await api.put(`/cases/${caseData.case_id}/sharing`, {
        sharing_level: newLevel,
        marketplace_desired_outcome:
          caseData.marketplace_desired_outcome ||
          caseData.questionnaire?.relief_requested ||
          "",
      });
      setLevel(newLevel);
      toast.success(
        newLevel === "private"
          ? "Case is now private"
          : "Sharing enabled. Fine-tune the summary in the Share tab."
      );
      if (typeof onChange === "function") onChange(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update sharing");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={busy}
          className={`inline-flex items-center gap-2 border px-3 py-1.5 font-mono-tech text-[11px] uppercase tracking-wider hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-900 ${current.tone}`}
          data-testid="case-sharing-badge"
          aria-label={`Case sharing: ${current.label}`}
        >
          <Icon className="w-3.5 h-3.5" strokeWidth={2} />
          <span>Sharing: {current.label}</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 rounded-none" data-testid="case-sharing-menu">
        <DropdownMenuLabel className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500">
          Who can see this case?
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {OPTIONS.map((o) => {
          const IconOpt = o.icon;
          const active = o.value === level;
          return (
            <DropdownMenuItem
              key={o.value}
              onClick={() => setLevelRemote(o.value)}
              className="rounded-none py-2 px-3 cursor-pointer focus:bg-slate-50"
              data-testid={`sharing-option-${o.value}`}
            >
              <div className="flex items-start gap-3 w-full">
                <IconOpt className="w-4 h-4 mt-0.5 text-slate-700" strokeWidth={1.75} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 text-sm">{o.label}</span>
                    {active && <Check className="w-3.5 h-3.5 text-emerald-600" />}
                  </div>
                  <div className="text-xs text-slate-600 leading-snug">{o.blurb}</div>
                </div>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
